import React, { useEffect, useState } from "react";
import { Box, Grid, MenuItem, Tooltip, TooltipProps } from "@mui/material";
import { tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useMutation } from "@tanstack/react-query";
import * as crypto from "crypto-js";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import Button, { Color } from "../../../../../../components/Form/Button";
import ImageInput from "../../../../../../components/Form/ImageInput";
import TextField from "../../../../../../components/Form/Input";
import PhoneInput from "../../../../../../components/Form/PhoneInput";
import Select from "../../../../../../components/Form/Select";
import AddressInfoForm from "../../../../../../components/Generic/AddressComponent/AddressInfoForm";
import SkeletonComponent from "../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import LoadingModal from "../../../../../../components/LoadingModal";
import { BranchesField } from "../../../../../../components/OnBoarding/BranchesField";
import { useAppState } from "../../../../../../context/StateContext";
import useYupValidation from "../../../../../../hooks/useYupValidation";
import { Company, Logo } from "../../../../../../models/company";
import { DifferentLocation as LocationAddress } from "../../../../../../models/location";
import CompanyService from "../../../../../../services/company.service";
import { CompanyProfile, Status } from "../../../../../../util/enums";
import FooterActions from "./ActionButtons";
import { FormContact } from "./FormContact";
import useStyles, {
  CompanyInfoLabel,
  CompanyLogoAndIconInfo,
  CompanyLogoAndIconTooltipContainer,
  CompanySlugContainer,
  CompanySlugInfo,
  CompanySlugSubdomain,
  CompanySlugUrl,
  DetailsFieldTitle,
  Separator,
} from "./styles";
import { AppTimezoneSelect } from "Components/Generic/AppTimezoneSelect";

interface Props {
  errors: yup.ValidationError[];
  companyInformation?: Company;
  loading: boolean;
  isUpdating: boolean;
  refetchCompany?: () => void;
  update: (value: Company) => void;
  handleSubmit?: () => void;
  isOnBoardingPage?: boolean;
  permission?: {
    edit: boolean;
  };
}

/** classe local só para pintar os labels no onboarding */
const useLocalOnboardingStyles = makeStyles(() => ({
  onboardingBlueLabel: {
    color: "#0055A4 !important",
    fontWeight: 600,
  },
}));

const SHIPEEZI_DOMAIN = ".shipeezi.com";

export const FormCompany = (props: Props) => {
  const classes = useStyles();
  const local = useLocalOnboardingStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const {
    companyInformation,
    refetchCompany,
    loading,
    isUpdating,
    errors,
    update,
    handleSubmit,
    isOnBoardingPage = false,
    permission,
  } = props;

  /** dimensões específicas do onboarding */
  const W = {
    upload: 399.5,
    inputTallH: 40,
    nameW: 529.5,
    regW: 529.5,
    phoneW: 345,
    emailW: 345,
    webW: 345,
  };

  /** altura 40px para OutlinedInput + centralização do texto */
  const inputTallSx = isOnBoardingPage
    ? {
        "& .MuiOutlinedInput-root": { height: `${W.inputTallH}px` },
        "& .MuiOutlinedInput-input": {
          height: `${W.inputTallH}px`,
          padding: "0 12px",
          display: "flex",
          alignItems: "center",
        },
      }
    : undefined;

  const phoneTallSx = inputTallSx;

  const isMV = currentUser?.company?.profile?.initials === CompanyProfile.MV;
  const uploadCol = isOnBoardingPage ? 4 : 6;
  const showBranches = !isOnBoardingPage && isMV;

  const [token, setToken] = useState<string>("");
  const [hasFormBeenChanged, setHasFormBeenChanged] = useState<boolean>(false);
  const [isAddressExpanded, setIsAddressExpanded] = useState<boolean>(true);

  const sizeOnboarding = isOnBoardingPage ? 4 : 6;
  const isShipmentTracking = currentUser?.company?.profile?.initials === CompanyProfile.ST;

  const { mutate, isLoading } = useMutation(
    ["company.update"],
    ({ id, company }: { id: number; company: any }) => CompanyService.update(id, company),
    {
      onSuccess: () => {
        NotificationToast.success(translate("companyUpdatedSuccessfully"));
      },
    }
  );

  const getAcceptedTermsInformation = () => {
    if (!companyInformation || !companyInformation.termsAcceptedUser) return "";
    const { termsAcceptedUser: user } = companyInformation;
    if (!user || !user.name) return "";

    const onText = translate("on").toLowerCase();
    const byText = translate("by").toLowerCase();
    const atText = translate("at").toLowerCase();

    const date = companyInformation.createdAt ? new Date(companyInformation.createdAt) : null;
    if (!date) return "";

    const tiemstampDate = `${format(date, "dd/MM/yyyy")}`;
    const timestampTime = `${format(date, "hh:mma")}`;

    return ` ${onText} ${tiemstampDate} ${byText} ${user.name} ${
      user.lastName || ""
    } ${atText} ${timestampTime}.`;
  };

  const handleGenerateOrCopyToken = () => {
    if (token.length === 0) {
      const randomString = crypto.MD5(
        `${companyInformation?.name} ${new Date().toLocaleDateString("pt-BR")}`
      );
      setToken(randomString.toString());
      if (companyInformation)
        mutate({
          id: Number(companyInformation?.id),
          company: { ...companyInformation, token: randomString.toString() },
        });
    } else {
      navigator.clipboard.writeText(token);
      NotificationToast.success(translate("copiedSuccessfully"));
    }
  };

  const handleSetAddressValue = (address?: LocationAddress[]) => {
    setHasFormBeenChanged(true);
    if (address) {
      const addressUpdated = {
        location: {
          ...address[0],
          companyId: currentUser?.companyId,
          valid: true,
          status: Status.ACTIVE,
        },
        ...(address[0]?.id ? { locationId: address[0].id } : {}),
      } as Company;

      update(
        companyInformation
          ? {
              ...companyInformation,
              ...addressUpdated,
            }
          : addressUpdated
      );
    }
  };

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  useEffect(() => {
    if (companyInformation) {
      if (companyInformation?.phone) {
        if (
          !companyInformation?.phone?.includes("+") &&
          !isValidPhoneNumber(companyInformation?.phone) &&
          companyInformation?.phone?.length > 0
        ) {
          update({
            ...companyInformation,
            phone: `+${companyInformation.phone}`,
          });
        }
      }
      if (companyInformation.token) {
        setToken(companyInformation.token);
      }
    }
  }, [companyInformation]);

  const CompanyLogoAndIconTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      color: "#FFFFFF",
      maxWidth: 370,
      fontFamily: "Manrope",
      fontStyle: "normal",
      fontWeight: 600,
      fontSize: "14px",
      lineHeight: "24px",
      padding: "8px 12px",
    },
  }));

  const COMPANY_TIMEZONE_IS_ENABLED = false;

  return (
    <Grid
      container
      direction="row"
      data-cy="companyForm"
      className={!isOnBoardingPage ? classes.companyForm : undefined}
      sx={isOnBoardingPage ? { width: "100%", maxWidth: "none", mx: 0, px: 0 } : undefined}
    >
      <Grid
        container
        direction="row"
        item
        xs={12}
        spacing={2}
        className={classes.companyFormContainer}
        sx={{ px: isOnBoardingPage ? 0 : undefined }}
      >
        {/* Uploads — no onboarding INVERTE: ICON → LOGO */}
        {isOnBoardingPage ? (
          <>
            <Grid item xs={12} sm={uploadCol} sx={{ maxWidth: W.upload }}>
              <SkeletonComponent isLoading={loading}>
                <>
                  <DetailsFieldTitle>{translate("businessIcon")}</DetailsFieldTitle>
                  <Box sx={{ width: W.upload, "& .dropzone": { minHeight: 80, height: 80 } }}>
                    <ImageInput
                      disabled={permission && !permission.edit}
                      id="company-icon"
                      classname={classes.iconContainer}
                      title=""
                      logo={companyInformation?.icon}
                      onChangeLogo={(logo?: Logo) => {
                        setHasFormBeenChanged(true);
                        update(
                          companyInformation
                            ? { ...companyInformation, icon: logo }
                            : ({ icon: logo } as Company)
                        );
                      }}
                      cropImage
                      cropAspect={4 / 4}
                      cropShape="round"
                    />
                  </Box>
                </>
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12} sm={uploadCol} sx={{ maxWidth: W.upload }}>
              <SkeletonComponent isLoading={loading}>
                <>
                  <DetailsFieldTitle>{translate("businessLogo")}</DetailsFieldTitle>
                  <Box sx={{ width: W.upload, "& .dropzone": { minHeight: 80, height: 80 } }}>
                    <ImageInput
                      disabled={permission && !permission.edit}
                      id="company-logo"
                      title=""
                      logo={companyInformation?.logo}
                      onChangeLogo={(logo?: Logo) => {
                        setHasFormBeenChanged(true);
                        update(
                          companyInformation
                            ? { ...companyInformation, logo }
                            : ({ logo } as Company)
                        );
                      }}
                    />
                  </Box>
                </>
              </SkeletonComponent>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={uploadCol}>
              <SkeletonComponent isLoading={loading}>
                <>
                  <DetailsFieldTitle>{translate("businessLogo")}</DetailsFieldTitle>
                  <ImageInput
                    disabled={permission && !permission.edit}
                    id="company-logo"
                    title={""}
                    logo={companyInformation?.logo}
                    onChangeLogo={(logo?: Logo) => {
                      setHasFormBeenChanged(true);
                      update(
                        companyInformation
                          ? {
                              ...companyInformation,
                              logo,
                            }
                          : ({ logo } as Company)
                      );
                    }}
                  />
                </>
              </SkeletonComponent>
            </Grid>
            <Grid item xs={12} sm={uploadCol}>
              <SkeletonComponent isLoading={loading}>
                <>
                  <DetailsFieldTitle>{translate("businessIcon")}</DetailsFieldTitle>
                  <ImageInput
                    disabled={permission && !permission.edit}
                    id="company-icon"
                    classname={classes.iconContainer}
                    title={""}
                    logo={companyInformation?.icon}
                    onChangeLogo={(logo?: Logo) => {
                      setHasFormBeenChanged(true);
                      update(
                        companyInformation
                          ? {
                              ...companyInformation,
                              icon: logo,
                            }
                          : ({ icon: logo } as Company)
                      );
                    }}
                    cropImage
                    cropAspect={4 / 4}
                    cropShape="round"
                  />
                </>
              </SkeletonComponent>
            </Grid>
          </>
        )}

        {/* ——— Separator abaixo do header do step (mantém seu estilo de margin: 0) ——— */}
        {isOnBoardingPage && (
          <Grid item xs={12} sx={{ px: 0 }}>
            <Separator
              style={{
                border: 0,
                borderTop: "1px solid #E4E7EC",
                margin: "0",
                display: "block",
                position: "static",
              }}
            />
          </Grid>
        )}

        {!loading && (
          <Grid container item direction="row" sx={{ px: 0 }}>
            <DetailsFieldTitle>{translate("details")}</DetailsFieldTitle>
          </Grid>
        )}

        {!loading && (
          <CompanyLogoAndIconTooltip
            title={
              <CompanyLogoAndIconTooltipContainer container>
                <Grid item>
                  <FiInfo color="#FFFFFF" size={12} />
                </Grid>
                <Grid item xs>
                  {translate(
                    "yourCompanyLogoAndIconRepresentYourBrandIdentityAndValuesThisWillBeUsedInAllCommunicationsAndDocumentsSoItWouldBeGoodToUpload"
                  )}
                </Grid>
              </CompanyLogoAndIconTooltipContainer>
            }
            placement="top-end"
          >
            <CompanyLogoAndIconInfo>
              <FiInfo color="#308CDB" size={12} />
            </CompanyLogoAndIconInfo>
          </CompanyLogoAndIconTooltip>
        )}

        {/* Linha 1 */}
        <Grid
          item
          xs={12}
          sm={sizeOnboarding}
          sx={isOnBoardingPage ? { width: W.nameW } : undefined}
        >
          <SkeletonComponent isLoading={loading}>
            <TextField
              disabled={permission && !permission.edit}
              name="companyName"
              data-cy="companyBusinessName"
              placeholder={translate("shipeeziAustralia")}
              label={isShipmentTracking ? translate("entityName") : translate("businessName")}
              value={companyInformation?.name || ""}
              required
              error={fieldHasError("name")}
              errorMessage={getErrorByField("name")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHasFormBeenChanged(true);
                update(
                  companyInformation
                    ? { ...companyInformation, name: e.target.value }
                    : ({ name: e.target.value } as Company)
                );
              }}
              labelClassName={isOnBoardingPage ? local.onboardingBlueLabel : undefined}
              sx={inputTallSx}
            />
          </SkeletonComponent>
        </Grid>

        <Grid
          item
          xs={12}
          sm={sizeOnboarding}
          sx={isOnBoardingPage ? { width: W.regW } : undefined}
        >
          <SkeletonComponent isLoading={loading}>
            <TextField
              disabled={permission && !permission.edit}
              name="companyNumber"
              data-cy="registrationNumber"
              placeholder="123456"
              label={translate("businessRegistration")}
              error={fieldHasError("registrationNumber")}
              errorMessage={getErrorByField("registrationNumber")}
              value={companyInformation?.registrationNumber}
              required
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setHasFormBeenChanged(true);
                const { value } = event.target;
                const valueFormatted = value.trim() !== "" ? value : value.trim();

                update(
                  companyInformation
                    ? {
                        ...companyInformation,
                        registrationNumber: valueFormatted,
                      }
                    : ({ registrationNumber: valueFormatted } as Company)
                );
              }}
              labelClassName={isOnBoardingPage ? local.onboardingBlueLabel : undefined}
              sx={inputTallSx}
            />
          </SkeletonComponent>
        </Grid>

        {isOnBoardingPage && currentUser?.company?.profile?.initials === CompanyProfile.MV && (
          <Grid item sm={4} />
        )}

        {!isOnBoardingPage && (
          <Grid item xs={12} sm={sizeOnboarding}>
            <SkeletonComponent isLoading={loading}>
              <Select
                name="companyType"
                dataCy="companyType"
                label={translate("companyType", { defaultValue: "Company Type" })}
                value={companyInformation?.isMilitary === true ? "military" : "commercial"}
                disabled={permission && !permission.edit}
                onChange={(e: any) => {
                  setHasFormBeenChanged(true);
                  const isMilitary = e.target.value === "military";
                  update(
                    companyInformation
                      ? { ...companyInformation, isMilitary }
                      : ({ isMilitary } as Company)
                  );
                }}
              >
                <MenuItem value="commercial">
                  {translate("commercial", { defaultValue: "Commercial" })}
                </MenuItem>
                <MenuItem value="military">
                  {translate("military", { defaultValue: "Military" })}
                </MenuItem>
              </Select>
            </SkeletonComponent>
          </Grid>
        )}

        {/* Linha 2 */}

        {!isOnBoardingPage && currentUser?.company?.profile?.initials === CompanyProfile.MV && (
          <Grid item xs={12}>
            <SkeletonComponent isLoading={loading}>
              <CompanySlugContainer>
                <CompanyInfoLabel>{translate("customerPortalAddress")}</CompanyInfoLabel>
                <CompanySlugInfo>{translate("definesLinkYourCustomerWillUse")}</CompanySlugInfo>
                <CompanySlugSubdomain>
                  <CompanySlugUrl>https://</CompanySlugUrl>
                  <TextField
                    disabled={permission && !permission.edit}
                    name="companySubdomain"
                    data-cy="companySubdomain"
                    value={(
                      companyInformation?.subdomain ??
                      companyInformation?.companySubdomain?.subdomain ??
                      ""
                    ).replace(SHIPEEZI_DOMAIN, "")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setHasFormBeenChanged(true);
                      const { value } = event.target;
                      const formattedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, "");

                      update(
                        companyInformation
                          ? {
                              ...companyInformation,
                              subdomain: formattedValue,
                            }
                          : ({ subdomain: formattedValue } as Company)
                      );
                    }}
                  />
                  <CompanySlugUrl>{SHIPEEZI_DOMAIN}</CompanySlugUrl>
                </CompanySlugSubdomain>
              </CompanySlugContainer>
            </SkeletonComponent>
          </Grid>
        )}

        <Grid item xs={12} sm={4} sx={isOnBoardingPage ? { width: W.phoneW } : undefined}>
          <SkeletonComponent isLoading={loading}>
            <PhoneInput
              disabled={permission && !permission.edit}
              label={
                isOnBoardingPage
                  ? translate("phoneNumber", { defaultValue: "Phone number" })
                  : translate("phone")
              }
              labelStyle={isOnBoardingPage ? local.onboardingBlueLabel : ""} // classe
              name="companyPhone"
              data-cy="companyPhone"
              placeholder="61430111900"
              error={fieldHasError("phone")}
              errorMessage={getErrorByField("phone")}
              international
              defaultCountry="AU"
              value={companyInformation?.phone || ""}
              required={true}
              minLength={2}
              countryCallingCodeEditable
              onChange={(value) => {
                setHasFormBeenChanged(true);
                update(
                  companyInformation
                    ? { ...companyInformation, phone: value }
                    : ({ phone: value } as Company)
                );
              }}
              sx={phoneTallSx}
            />
          </SkeletonComponent>
        </Grid>

        <Grid item xs={12} sm={4} sx={isOnBoardingPage ? { width: W.emailW } : undefined}>
          <SkeletonComponent isLoading={loading}>
            <TextField
              disabled={permission && !permission.edit}
              name="companyEmail"
              data-cy="companyEmail"
              label={translate("mainEmailAddress")}
              placeholder={translate("supportShipeeziCom")}
              error={fieldHasError("email")}
              errorMessage={getErrorByField("email")}
              value={companyInformation?.email || ""}
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHasFormBeenChanged(true);
                update(
                  companyInformation
                    ? { ...companyInformation, email: e.target.value }
                    : ({ email: e.target.value } as Company)
                );
              }}
              labelClassName={isOnBoardingPage ? local.onboardingBlueLabel : undefined}
              sx={inputTallSx}
            />
          </SkeletonComponent>
        </Grid>

        {isOnBoardingPage ? (
          <Grid item xs={12} sm={4} sx={{ width: W.webW }}>
            <SkeletonComponent isLoading={loading}>
              <TextField
                disabled={permission && !permission.edit}
                name="companyWebsite"
                data-cy="website"
                placeholder={translate("wwwShipeeziCom")}
                label={translate("website")}
                error={fieldHasError("webSite")}
                errorMessage={getErrorByField("webSite")}
                value={companyInformation?.webSite || ""}
                required={false} // 🔹 opcional no onboarding
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setHasFormBeenChanged(true);
                  update(
                    companyInformation
                      ? { ...companyInformation, webSite: e.target.value }
                      : ({ webSite: e.target.value } as Company)
                  );
                }}
                labelClassName={local.onboardingBlueLabel}
                sx={inputTallSx}
              />
            </SkeletonComponent>
          </Grid>
        ) : (
          <>
            {COMPANY_TIMEZONE_IS_ENABLED && (
              <Grid item xs={12} sm={4}>
                <AppTimezoneSelect
                  isLoading={loading}
                  value={companyInformation?.timezone}
                  handleChange={(timezone) => {
                    update(
                      companyInformation
                        ? {
                            ...companyInformation,
                            timezoneId: timezone?.id,
                            timezone,
                          }
                        : ({ timezoneId: timezone?.id, timezone } as Company)
                    );
                  }}
                />
              </Grid>
            )}
          </>
        )}

        {!isOnBoardingPage && (
          <Grid item xs={12} sm={4}>
            <AppTimezoneSelect
              isLoading={loading}
              value={companyInformation?.timezone}
              handleChange={(timezone) => {
                update(
                  companyInformation
                    ? {
                        ...companyInformation,
                        timezoneId: timezone?.id,
                        timezone,
                      }
                    : ({ timezoneId: timezone?.id, timezone } as Company)
                );
              }}
            />
          </Grid>
        )}

        {!isOnBoardingPage && (
          <Grid item xs={12} sm={4}>
            <SkeletonComponent isLoading={loading}>
              <TextField
                disabled={permission && !permission.edit}
                name="companyWebsite"
                data-cy="website"
                placeholder={translate("wwwShipeeziCom")}
                label={translate("website")}
                error={fieldHasError("webSite")}
                errorMessage={getErrorByField("webSite")}
                value={companyInformation?.webSite || ""}
                required={!isOnBoardingPage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setHasFormBeenChanged(true);
                  update(
                    companyInformation
                      ? { ...companyInformation, webSite: e.target.value }
                      : ({ webSite: e.target.value } as Company)
                  );
                }}
              />
            </SkeletonComponent>
          </Grid>
        )}

        {/* Business Address */}
        <Grid item xs={12} sx={{ mt: 1, px: 0 }}>
          <SkeletonComponent isLoading={loading}>
            <>
              <div className={classes.businessAddressLabelContainer}>
                <span className={classes.businessLabel}>{translate("businessAddress")}</span>
              </div>

              <AddressInfoForm
                containerClass="p-0"
                addressesArray={(() => {
                  const addressArray = companyInformation?.location
                    ? [companyInformation?.location]
                    : ([{}] as LocationAddress[]);

                  return addressArray;
                })()}
                hasToShowOnlyOne
                isExpandedAddress={isAddressExpanded}
                setIsExpandedAddress={setIsAddressExpanded}
                condition=""
                required={isOnBoardingPage}
                hasError={fieldHasError("location")}
                errorMessage={getErrorByField("location")}
                update={(value: LocationAddress[]) => handleSetAddressValue(value)}
              />

              {isOnBoardingPage && <Box aria-hidden sx={{ height: 160 }} />}
            </>
          </SkeletonComponent>
        </Grid>

        {/* Branches somente fora do onboarding */}
        {showBranches && <BranchesField loading={loading} />}

        {!isOnBoardingPage && (
          <>
            <Grid item xs={12}>
              <SkeletonComponent isLoading={loading}>
                <FormContact
                  companyContacts={
                    companyInformation?.contacts && companyInformation?.contacts.length > 0
                      ? companyInformation?.contacts.filter((contact) =>
                          contact.valid === true && contact.customerId === null
                            ? !(contact.user && contact.user.status === Status.INACTIVE)
                            : false
                        )
                      : []
                  }
                  refetchCompanyContacts={() => refetchCompany && refetchCompany()}
                  permission={permission}
                />
              </SkeletonComponent>
            </Grid>

            <Grid item xs={12}>
              <hr />
              <SkeletonComponent isLoading={loading}>
                <>
                  <div className={classes.businessLabelContainer}>
                    <span className={classes.businessLabel}>{translate("shipeeziNetwork")}</span>
                  </div>
                  <Grid
                    item
                    container
                    xs={12}
                    sm={5}
                    alignItems="flex-end"
                    spacing={2}
                    className="mb-3"
                  >
                    <Grid item md={9} lg={9}>
                      <TextField
                        name="shipeeziKey"
                        data-cy="generalShipeeziKey"
                        value={token}
                        label={translate("shipeeziKey")}
                        disabled
                      />
                    </Grid>
                    <Grid item md={3} lg={3}>
                      <Button
                        customColor={Color.SECONDARY}
                        onClick={() => handleGenerateOrCopyToken()}
                        loading={isLoading}
                      >
                        {token.length === 0 ? translate("generateToken") : translate("copyToken")}
                      </Button>
                    </Grid>
                  </Grid>
                  {companyInformation && companyInformation.termsAcceptedUser && (
                    <span className={classes.termsAccepted}>
                      {translate("termsOfUsePrivacyPolicySecurityPolicy")}
                      {translate("andSaasAgreement")}
                      {translate("agreed")}
                      {getAcceptedTermsInformation()}
                    </span>
                  )}
                </>
              </SkeletonComponent>
            </Grid>

            {permission && permission.edit && (
              <Grid item xs={12}>
                <SkeletonComponent isLoading={loading}>
                  <FooterActions
                    isLoading={isUpdating}
                    changeHasBeenMade={hasFormBeenChanged}
                    handleGetOldCompanyInfo={() => refetchCompany && refetchCompany()}
                    handleUpdateCompany={() => handleSubmit && handleSubmit()}
                  />
                </SkeletonComponent>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <LoadingModal showing={isUpdating} />
    </Grid>
  );
};
